import { useState } from "react"
import { errorToast } from "@/v2-ui/toast"
import Selector from "@/v2-backoffice-client/components/selector"
import type { UserFragment } from "@/v2-backoffice-client/user/__types__/user.fragment"
import type { SelectorTriggerProps } from "@/v2-backoffice-client/components/selector"
import { useUserQuery } from "@/v2-backoffice-client/user/user.query"
import { formatUserName } from "@/v2-backoffice-client/user/user.utils"

export type UserPickerProps = Omit<SelectorTriggerProps, "onSelect"> & {
  onSelect(user: UserFragment): void
}

function UserPicker(props: UserPickerProps) {
  const { onSelect, ...triggerProps } = props

  const [search, setSearch] = useState("")

  const { data, loading, error } = useUserQuery({
    skip: search.length < 2,
    variables: {
      input: {
        limit: 10,
        filters:
          search.length >= 2
            ? [{ column: "Email", contains: search }]
            : undefined,
      },
    },
  })

  errorToast(error, {
    title: "Failed to query for users",
  })

  const users = data?.getUsers.items ?? []

  return (
    <Selector>
      <Selector.Trigger iconSrc="search" {...triggerProps}>
        {triggerProps.children ?? "Search users..."}
      </Selector.Trigger>

      <Selector.Content>
        <Selector.Search
          placeholder="Search by email..."
          onSearch={setSearch}
        />

        <Selector.List
          emptyLabel="No users found"
          isLoading={loading}
          error={error}
          noResults={users.length === 0}
        >
          {users.map((user) => (
            <Selector.Item
              key={user.Id}
              value={String(user.Id)}
              onSelect={() => {
                onSelect(user)
              }}
            >
              <Selector.ItemText
                primary={formatUserName(user.FirstName, user.LastName)}
                secondary={user.Email ?? undefined}
              />
            </Selector.Item>
          ))}
        </Selector.List>
      </Selector.Content>
    </Selector>
  )
}

export default UserPicker
